import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, FileText, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharedReportPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await axios.get(`${API}/shared/${token}`);
        setReportData(response.data);
      } catch (err) {
        console.error("Error fetching shared report:", err);
        if (err.response?.status === 404) {
          setError("This link is invalid or has been deleted.");
        } else if (err.response?.status === 410) {
          setError("This link has expired.");
        } else {
          setError("Failed to load report. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Link Error</h1>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const brandColors = reportData?.client?.brand_settings || {
    primary_color: "#e066ff",
    secondary_color: "#0F172A",
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shared-report-page">
      {/* Header */}
      <header
        className="py-6 px-8"
        style={{ backgroundColor: brandColors.secondary_color }}
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {reportData?.client?.brand_settings?.logo_base64 && (
              <img
                src={reportData.client.brand_settings.logo_base64}
                alt="Logo"
                className="h-10"
              />
            )}
            <div>
              <h1 className="text-xl font-semibold text-white">
                {reportData?.client?.name || "Marketing Report"}
              </h1>
              <p className="text-sm text-white/70">
                Generated {new Date(reportData?.generated_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <Badge className="bg-white/20 text-white">
            {reportData?.report_type?.replace(/_/g, " ") || "Report"}
          </Badge>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto py-8 px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="kpi-card">
            <CardContent className="pt-6 text-center">
              <div
                className="w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center"
                style={{ backgroundColor: `${brandColors.primary_color}20` }}
              >
                <BarChart3
                  className="w-6 h-6"
                  style={{ color: brandColors.primary_color }}
                />
              </div>
              <p className="text-3xl font-bold" style={{ color: brandColors.secondary_color }}>
                --
              </p>
              <p className="text-sm text-slate-500">Total Sessions</p>
            </CardContent>
          </Card>

          <Card className="kpi-card">
            <CardContent className="pt-6 text-center">
              <div
                className="w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center"
                style={{ backgroundColor: `${brandColors.primary_color}20` }}
              >
                <FileText
                  className="w-6 h-6"
                  style={{ color: brandColors.primary_color }}
                />
              </div>
              <p className="text-3xl font-bold" style={{ color: brandColors.secondary_color }}>
                --
              </p>
              <p className="text-sm text-slate-500">MQLs Generated</p>
            </CardContent>
          </Card>

          <Card className="kpi-card">
            <CardContent className="pt-6 text-center">
              <div
                className="w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center"
                style={{ backgroundColor: `${brandColors.primary_color}20` }}
              >
                <BarChart3
                  className="w-6 h-6"
                  style={{ color: brandColors.primary_color }}
                />
              </div>
              <p className="text-3xl font-bold" style={{ color: brandColors.secondary_color }}>
                --
              </p>
              <p className="text-sm text-slate-500">Pipeline Value</p>
            </CardContent>
          </Card>
        </div>

        <Card className="chart-container">
          <CardHeader>
            <CardTitle>Report Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500">
                This is a shared report preview
              </p>
              <p className="text-sm text-slate-400">
                Full report data will be displayed here
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-400">
          <p>Powered by JCI Analytics</p>
          <p>by JCI Marketing</p>
        </div>
      </main>
    </div>
  );
};

export default SharedReportPage;
